import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  LinearProgress,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Divider,
  Paper,
  IconButton,
  Avatar,
  makeStyles,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import moment from 'moment';
import { Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useRequest } from 'src/hooks';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4)
  },
  headerTextMargin: {
    fontWeight: 500,
    marginLeft: theme.spacing(2)
  },

  tableContainer: {
    margin: '0 auto',
    // marginTop: theme.spacing(2),
    maxWidth: 1700,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    overflowX: 'auto'
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: theme.spacing(2)
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  },
  tableCell: {
    padding: theme.spacing(2),
    // textAlign: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    padding: theme.spacing(2),
    fontWeight: 'bold',
    textAlign: 'center',
    color: theme.palette.primary.main
  }
}));

/**
 * @typedef {{date:string,ixUser:number,items:Array<[string, [string, string]]>}} log
 */

/**
 * @param {object} props
 * @param {{key:number,value:log}} props.log
 */
const Card = ({ log }) => {
  const classes = useStyles();
  const { users = [] } = useSelector(state => state.bizMeta);
  const user = users.find(user => user.ixUser === log.value.ixUser);

  return (
    <Box
      component={Paper}
      p={1}
      display="flex"
      flexDirection="column"
      gridGap={1}
    >
      <Box display="flex" gridGap={2} alignItems="center">
        <Avatar
          className={classes.avatar}
          src={user ? `/api/images/user/${user.username}/avatar.jpg` : null}
        >
          {user ? user.FName[0].toUpperCase() : null}
        </Avatar>
        <Box display="flex" flexDirection="column" ml={2}>
          {Boolean(user) && (
            <Typography variant="caption" className={classes.headerTextMargin}>
              {user.FName + ' ' + user.LName}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary">
            {moment(log.date).format('MMM DD, YYYY h:mm a')}
          </Typography>
        </Box>
      </Box>
      <Box py={1}>
        <Divider />
      </Box>
      {log.value.items.map(logItem => (
        <Box key={logItem[0]} mt={1}>
          <Typography variant="caption" color="textSecondary">
            {logItem[0]}
          </Typography>
          <Typography variant="body2">
            {`${logItem[1][0]} -> ${logItem[1][1]}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * @param {object} props
 * @param {Array<log>} props.data
 * @param {boolean} open
 * @param {boolean} isLoading
 * @param {VoidFunction} onClose
 */
const TabularLogs = ({
  data = [],
  settings = {},
  open = false,
  isLoading = false,
  onClose = () => {}
}) => {
  const Request = useRequest();
  const classes = useStyles();
  const { users = [] } = useSelector(state => state.bizMeta);
  const { userToken, base_url } = useSelector(state => state.auth);
  const { flds = [], kvs_flds = [], labels = {} } =
    settings?.tabular_logs || {};

  // Normalize label keys so lookups are case-insensitive (handles ixSubLink vs ixSublink typos)
  const normalizedLabels = useMemo(() => {
    const map = {};
    Object.entries(labels || {}).forEach(([k, v]) => {
      map[k.toLowerCase()] = v;
    });
    return map;
  }, [labels]);

  const allowedFields = [...new Set([...kvs_flds, ...flds])].filter(
    f => f !== 'ixUser' && f !== 'pr.ixUser'
  );

  const [resolvedSubs, setResolvedSubs] = useState({});
  const fetchedRef = useRef(false);

  // fetch sub acc when dialog opens
  useEffect(() => {
    if (!open || !data.length || fetchedRef.current) return;

    fetchedRef.current = true;
    const ixSubsSet = new Set();

    // collect lahat ng ixSub na kailangan
    data.forEach(entry => {
      allowedFields.forEach(field => {
        const item = entry.value.items.find(([f]) => f === field);
        if (!item) return;
        const [oldVal, newVal] = item[1] || [];
        if (
          oldVal &&
          (field.startsWith('ixSubParent') || field.startsWith('ixSubLink'))
        )
          ixSubsSet.add(oldVal);
        if (
          newVal &&
          (field.startsWith('ixSubParent') || field.startsWith('ixSubLink'))
        )
          ixSubsSet.add(newVal);
      });
    });

    const fetchAllSubs = async () => {
      if (ixSubsSet.size === 0) return;

      try {
        const res = await Request.post('/lib/sub/multiple', {
          items: Array.from(ixSubsSet)
        });

        if (res.success && res.data?.items) {
          // Extract display names from the response objects
          const displayNames = {};
          Object.entries(res.data.items).forEach(([key, value]) => {
            displayNames[key] = value.sSub || value.sub_title || key;
          });
          setResolvedSubs(displayNames);
        }
      } catch (error) {
        console.error('Error fetching multiple sub accounts:', error);
      }
    };

    fetchAllSubs();
  }, [open, data.length, base_url, userToken, allowedFields]);

  // reset when dialogs close
  useEffect(() => {
    if (!open) {
      fetchedRef.current = false;
      setResolvedSubs({});
    }
  }, [open]);

  const prettyField = field =>
    field
      .replace(/^pr\./, '')
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();

  const formatNumber = value => {
    if (value === null || value === undefined) return '-';
    const raw = String(value).trim();
    if (raw === '') return '-';
    const parsed = Number(raw.replace(/,/g, ''));
    if (Number.isFinite(parsed)) {
      return parsed.toLocaleString('en-US');
    }
    return value;
  };

  const getLabel = field =>
    labels[field] ||
    normalizedLabels[field.toLowerCase()] ||
    prettyField(field);

  const getValue = (items = [], field) => {
    const item = items.find(([f]) => f === field);
    if (!item) return '-';

    const [oldVal, newVal] = item[1] || [];

    // Resolve sub-account titles if available, otherwise use raw values
    const rawOld = resolvedSubs[oldVal] ?? oldVal ?? '-';
    const rawNew = resolvedSubs[newVal] ?? newVal ?? '-';

    const displayOld = formatNumber(rawOld);
    const displayNew = formatNumber(rawNew);

    return newVal !== undefined && oldVal !== newVal
      ? `${displayOld} → ${displayNew}`
      : displayOld;
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Tabular Logs</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      {isLoading && <LinearProgress />}
      <DialogContent style={{ padding: 16 }}>
        {/* <Box display="flex" flexDirection="column" gridGap={2}>
          {data.map(dataItem => (
            <Card key={dataItem.date} log={dataItem} />
          ))}
        </Box> */}
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.headerCell}>Edited by</TableCell>
                {allowedFields.map(field => (
                  <TableCell key={field} className={classes.headerCell}>
                    {getLabel(field)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || [])
                .sort((a, b) => new Date(b.value.date) - new Date(a.value.date))
                .map(entry => {
                  const user = users.find(u => u.ixUser === entry.value.ixUser);
                  return (
                    <TableRow key={entry.key} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        {user ? (
                          <Box display="flex" alignItems="center" gridGap={8}>
                            <Avatar
                              className={classes.avatar}
                              src={`/api/images/user/${user.username}/avatar.jpg`}
                            >
                              {user.FName?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box display="flex" flexDirection="column">
                              <Typography
                                variant="body1"
                                style={{ fontWeight: 'bold' }}
                              >
                                {user.FName} {user.LName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {moment(entry.value.date).format(
                                  'MMM DD, YYYY hh:mm a'
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {allowedFields.map(field => (
                        <TableCell key={field} className={classes.tableCell}>
                          {getValue(entry.value.items, field)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default TabularLogs;
